import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { makeStyles } from "@mui/styles";
import Colors from "../../theme/colors.json";
import { MenuItem, Select } from "@mui/material";
import { INITIAL_THEME } from "../../theme";
import { pushNotification } from "../../redux/actions";
import CONSTANTS, { Chart_Errors } from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { getCategoryPremium } from "../../services/ChartService";
import NoData from "./NoData";

const useStyles = makeStyles((theme) => ({
  responsiveContainer: {
    width: "100%",
    height: "100%",
    fontSize: theme.fontSizes.SMALL,
    // paddingRight: "2rem",
    paddingBottom: "2rem",
  },
  filterContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "3%",
  },
  filtersSection: {
    display: "flex",
    gap: "3%",
    // justifyContent: "space-around",
    // width: "50%",
  },
  selectStyles: {
    color: theme.palette.primary.main,
    fontSize: theme.fontSizes.SMALL,
    height: "1.6rem",
    minWidth: "6rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      fill: `${theme.palette.primary.main} !important`,
    },
  },

  graphHeading: {
    fontSize: theme.fontSizes.LARGE,
    fontWeight: 600,
    // width: "10%",
    // color: theme.palette.secondary.main,
  },

  selectRoot: {
    border: "unset",
    borderColor: theme.palette.primary.main,
  },

  icon: {
    fill: theme.palette.primary.main,
  },
}));

const PremiumChart = ({ years, productCategories }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [selectedYear, setSelectedYear] = useState(
    years[0]?.value || new Date().getFullYear()
  );

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [chartData, setChartData] = useState<any>([]);

  const [isLoading, setIsLoading] = useState(false);

  function getAbbreviatedMonth(month) {
    switch (month) {
      case "01":
        return "Jan";
      case "02":
        return "Feb";
      case "03":
        return "Mar";
      case "04":
        return "Apr";
      case "05":
        return "May";
      case "06":
        return "Jun";
      case "07":
        return "Jul";
      case "08":
        return "Aug";
      case "09":
        return "Sep";
      case "10":
        return "Oct";
      case "11":
        return "Nov";
      case "12":
        return "Dec";
      default:
        return "Invalid month";
    }
  }

  const handleGetPremium = async () => {
    setIsLoading(true);
    const response = await getCategoryPremium(selectedYear, selectedCategory);
    if (response?.success) {
      const formattedChartData = response?.data?.map((item) => {
        return {
          ...item,
          month: getAbbreviatedMonth(item?.month),
        };
      });
      setChartData(formattedChartData);
      setIsLoading(false);
      // dispatch(
      //   pushNotification({
      //     isOpen: true,
      //     message: response.message,
      //     type: CONSTANTS.SUCCESS,
      //   })
      // );
    } else {
      setChartData([]);
      setIsLoading(false);
      // dispatch(
      //   pushNotification({
      //     isOpen: true,
      //     message: response.message,
      //     type: CONSTANTS.ERROR,
      //   })
      // );
    }
  };

  useEffect(() => {
    handleGetPremium();
  }, [selectedYear, selectedCategory]);

  return (
    <>
      <div className={classes.filterContainer}>
        <div className={classes.graphHeading}>Premium</div>
        <div className={classes.filtersSection}>
          <Select
            size="small"
            className={classes.selectStyles}
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years?.map((item, index) => {
              return (
                <MenuItem key={index} value={item?.value}>
                  {item?.title}
                </MenuItem>
              );
            })}
          </Select>
          <Select
            size="small"
            className={classes.selectStyles}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {productCategories?.map((item, index) => {
              return (
                <MenuItem key={index} value={item?.value}>
                  {item?.title}
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
      {chartData?.length > 0 && !isLoading && (
        <ResponsiveContainer className={classes.responsiveContainer}>
          <LineChart
            width={500}
            height={300}
            data={chartData}
            // margin={{ top: 5, left: 0, right: 5, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            {/* <Legend /> */}
            <Line
              type="linear"
              dataKey="totalPremiumSum"
              stroke={INITIAL_THEME.palette.primary.main}
              activeDot={{ r: 8 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      {chartData?.length === 0 && !isLoading && (
        <NoData errorLabel={Chart_Errors.premium_error} />
      )}
    </>
  );
};

export default PremiumChart;
